import { z } from 'zod';
import { KgSyncModeSchema, KgVersionStatusSchema, SkillRunStatusSchema } from './workspace.js';
/**
 * v2 Knowledge Workspaces event catalog (Phase 1.2, v2 proposal §9.3).
 *
 * These are the events the metaphysics outbox writes and the console consumes
 * via SSE. Keeping the shapes typed here means both the server-side emitter
 * and the client-side consumer share one contract.
 *
 * Consumers today: the mockup fakes these locally; Phase 2 wires them to a
 * real outbox + dispatcher.
 */
const eventBaseSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    workspaceId: z.string(),
    atIso: z.string(), // ISO-8601 timestamp
});
// ─── document / upload lifecycle ──────────────────────────────────────────────
export const DocUploadedEventSchema = eventBaseSchema.extend({
    type: z.literal('doc.uploaded'),
    payload: z.object({
        itemId: z.string(),
        path: z.string(),
        sha256: z.string(),
        sizeBytes: z.number().int().nonnegative(),
        contentType: z.string().nullable(),
    }),
});
export const DocReplacedEventSchema = eventBaseSchema.extend({
    type: z.literal('doc.replaced'),
    payload: z.object({
        itemId: z.string(),
        path: z.string(),
        priorSha256: z.string(),
        sha256: z.string(),
    }),
});
export const DraftCommittedEventSchema = eventBaseSchema.extend({
    type: z.literal('draft.committed'),
    payload: z.object({
        versionId: z.string(),
        versionName: z.string(),
        itemCount: z.number().int().nonnegative(),
    }),
});
// ─── skill-run lifecycle ──────────────────────────────────────────────────────
export const SkillRunStartedEventSchema = eventBaseSchema.extend({
    type: z.literal('skill.run.started'),
    payload: z.object({
        runId: z.string(),
        skillId: z.string(),
        skillName: z.string(),
        inputItemIds: z.array(z.string()),
    }),
});
export const SkillRunProgressEventSchema = eventBaseSchema.extend({
    type: z.literal('skill.run.progress'),
    payload: z.object({
        runId: z.string(),
        status: SkillRunStatusSchema,
        progressPercent: z.number().int().min(0).max(100),
        message: z.string().nullable(),
    }),
});
export const SkillRunCompletedEventSchema = eventBaseSchema.extend({
    type: z.literal('skill.run.completed'),
    payload: z.object({
        runId: z.string(),
        skillId: z.string(),
        outputItemIds: z.array(z.string()),
        tokensIn: z.number().int().nonnegative(),
        tokensOut: z.number().int().nonnegative(),
        costUsd: z.number().nonnegative(),
    }),
});
export const SkillRunFailedEventSchema = eventBaseSchema.extend({
    type: z.literal('skill.run.failed'),
    payload: z.object({
        runId: z.string(),
        skillId: z.string(),
        error: z.string(),
    }),
});
export const ArtifactProducedEventSchema = eventBaseSchema.extend({
    type: z.literal('artifact.produced'),
    payload: z.object({
        itemId: z.string(),
        path: z.string(),
        role: z.string().nullable(),
        producedFromItemIds: z.array(z.string()),
        producedBySkillRunId: z.string(),
    }),
});
// ─── KG build lifecycle ───────────────────────────────────────────────────────
export const KgBuildStartedEventSchema = eventBaseSchema.extend({
    type: z.literal('kg.build.started'),
    payload: z.object({
        kgVersionId: z.string(),
        version: z.number().int().positive(),
        mode: KgSyncModeSchema,
        jobId: z.string(),
    }),
});
export const KgSyncItemCompletedEventSchema = eventBaseSchema.extend({
    type: z.literal('kg.sync.item.completed'),
    payload: z.object({
        kgVersionId: z.string(),
        itemId: z.string(),
        lightRagDocId: z.string().nullable(),
        tokensUsed: z.number().int().nonnegative(),
    }),
});
export const KgSyncItemFailedEventSchema = eventBaseSchema.extend({
    type: z.literal('kg.sync.item.failed'),
    payload: z.object({
        kgVersionId: z.string(),
        itemId: z.string(),
        error: z.string(),
    }),
});
export const KgVersionReadyEventSchema = eventBaseSchema.extend({
    type: z.literal('kg.version.ready'),
    payload: z.object({
        kgVersionId: z.string(),
        version: z.number().int().positive(),
        status: KgVersionStatusSchema,
        kgWorkspace: z.string(),
        entities: z.number().int().nonnegative(),
        relations: z.number().int().nonnegative(),
        chunks: z.number().int().nonnegative(),
        costUsd: z.number().nonnegative(),
        durationSec: z.number().int().nonnegative(),
    }),
});
export const KgVersionFailedEventSchema = eventBaseSchema.extend({
    type: z.literal('kg.version.failed'),
    payload: z.object({
        kgVersionId: z.string(),
        version: z.number().int().positive(),
        error: z.string(),
    }),
});
export const KgEntityNormalizedEventSchema = eventBaseSchema.extend({
    type: z.literal('kg.entity.normalized'),
    payload: z.object({
        kgVersionId: z.string(),
        mergeCount: z.number().int().nonnegative(),
    }),
});
// ─── discriminated union ──────────────────────────────────────────────────────
export const KnowledgeEventSchema = z.discriminatedUnion('type', [
    DocUploadedEventSchema,
    DocReplacedEventSchema,
    DraftCommittedEventSchema,
    SkillRunStartedEventSchema,
    SkillRunProgressEventSchema,
    SkillRunCompletedEventSchema,
    SkillRunFailedEventSchema,
    ArtifactProducedEventSchema,
    KgBuildStartedEventSchema,
    KgSyncItemCompletedEventSchema,
    KgSyncItemFailedEventSchema,
    KgVersionReadyEventSchema,
    KgVersionFailedEventSchema,
    KgEntityNormalizedEventSchema,
]);
/** All event type strings — useful for SSE subscription filters. */
export const knowledgeEventTypes = [
    'doc.uploaded',
    'doc.replaced',
    'draft.committed',
    'skill.run.started',
    'skill.run.progress',
    'skill.run.completed',
    'skill.run.failed',
    'artifact.produced',
    'kg.build.started',
    'kg.sync.item.completed',
    'kg.sync.item.failed',
    'kg.version.ready',
    'kg.version.failed',
    'kg.entity.normalized',
];
//# sourceMappingURL=knowledge-events.js.map